const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

class AIService {
  // Resume optimization for authenticated users
  async optimizeResume(resumeText, jobDescription = '', userId = '', targetIndustry = '', compressionRequested = false) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resume/optimize-full`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resume_text: resumeText,
          job_description: jobDescription,
          user_id: userId,
          target_industry: targetIndustry,
          compression_requested: compressionRequested
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to optimize resume');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Resume optimization error:', error);
      throw error;
    }
  }

  // Get resume preview for freemium users
  async getResumePreview(resumeText, jobDescription = '', userId = '', compressionRequested = false) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resume/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resume_text: resumeText,
          job_description: jobDescription,
          user_id: userId,
          compression_requested: compressionRequested
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get resume preview');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Resume preview error:', error);
      throw error;
    }
  }

  // Get career dashboard data
  async getCareerDashboard(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/career/dashboard/${userId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load dashboard');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Dashboard error:', error);
      throw error;
    }
  }

  // Get user analytics
  async getUserAnalytics(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/${userId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load analytics');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Analytics error:', error);
      throw error;
    }
  }

  // Get resume history
  async getResumeHistory(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/resume/history/${userId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load resume history');
      }

      const data = await response.json();
      
      // Return optimizations array (updated from "history")
      return {
        ...data,
        optimizations: data.optimizations || data.history || [] // Handle both old and new response format
      };
    } catch (error) {
      console.error('AI Service - Resume history error:', error);
      throw error;
    }
  }

  // Generate cover letter
  async generateCoverLetter(resumeText, jobDescription, companyName, userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cover-letter/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resume_text: resumeText,
          job_description: jobDescription,
          company_name: companyName,
          user_id: userId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate cover letter');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Cover letter error:', error);
      throw error;
    }
  }

  // Job matching
  async findJobMatches(resumeText, location, jobType, userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/job-matching/find`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resume_text: resumeText,
          location: location,
          job_type: jobType,
          user_id: userId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to find job matches');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Job matching error:', error);
      throw error;
    }
  }

  // Interview preparation
  async prepareInterview(jobDescription, resumeText, interviewType, userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/interview/prepare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          job_description: jobDescription,
          resume_text: resumeText,
          interview_type: interviewType,
          user_id: userId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to prepare interview');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Interview prep error:', error);
      throw error;
    }
  }

  // Get subscription status
  async getUserSubscription(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user/subscription/${userId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Subscription error:', error);
      throw error;
    }
  }

  // Get available plans
  async getPlans() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/plans`);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load plans');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Plans error:', error);
      throw error;
    }
  }

  // Create payment order
  async createPaymentOrder(planId, userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/payment/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: planId,
          user_id: userId,
          frontend_url: window.location.origin  // Add required frontend_url field
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create payment order');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Payment order error:', error);
      throw error;
    }
  }

  // Verify payment
  async verifyPayment(paymentId, orderId, signature, userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/payment/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          razorpay_payment_id: paymentId,
          razorpay_order_id: orderId,
          razorpay_signature: signature,
          user_id: userId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to verify payment');
      }

      return await response.json();
    } catch (error) {
      console.error('AI Service - Payment verification error:', error);
      throw error;
    }
  }
}

const aiService = new AIService();
export default aiService;